import { createContext, useContext } from "react";

/**
 * De locatie waarop de wizard Nieuwe reservering rekent. Een pension met meerdere vestigingen boekt niet altijd op
 * dezelfde: de klant kiest hem in de stap Periode, naast de beschikbaarheid, zodat meteen zichtbaar is of er op die
 * locatie in die periode plek is. De keuze stuurt alles wat per vestiging verschilt — tarieven, verblijftypen,
 * arrangementen, breng- en haaltijden en de prijs — en gaat daarom als {@code vestigingId} mee met elke aanroep.
 *
 * <p>Buiten de wizard (en in de tests die hem los renderen) is er geen keuze: dan blijft hij leeg en bepaalt de
 * server de vestiging zoals altijd — de vestiging van de medewerker, of die van het portaal.
 */
export interface Locatie {
  id: number;
  naam: string;
}

export interface LocatieKeuze {
  /** De gekozen locatie, of {@code null} zolang de server er niets over zei. */
  gekozen: number | null;
  /** Waaruit te kiezen valt; bij één locatie toont de wizard de keuze niet. */
  keuzes: Locatie[];
  kies: (id: number) => void;
}

export const LEEG: LocatieKeuze = { gekozen: null, keuzes: [], kies: () => {} };

export const LocatieContext = createContext<LocatieKeuze>(LEEG);

export function useLocatie(): LocatieKeuze {
  return useContext(LocatieContext);
}

/**
 * De gekozen locatie aan een wizard-url hangen. Zonder keuze blijft de url ongewijzigd, zodat niets verandert voor
 * een pension met één vestiging.
 */
export function metLocatie(url: string, vestigingId: number | null): string {
  if (vestigingId == null) {
    return url;
  }
  return `${url}${url.includes("?") ? "&" : "?"}vestigingId=${vestigingId}`;
}

/** Idem, als hook: de aanroeper hoeft de context niet zelf te lezen. */
export function useLocatieUrl(): (url: string) => string {
  const { gekozen } = useLocatie();
  return (url: string) => metLocatie(url, gekozen);
}
